import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SiteLogo from '../../components/Layout/SiteLogo';
import LanguageSwitcher from '../../components/Layout/LanguageSwitcher';
import { useSettings } from '../../contexts/SettingsContext';
import {
  buildReviewSummary,
  buildWizardSteps,
  emptyResponses,
  MAIN_GOAL_VALUES,
  VisaResponses,
  WizardStepId,
  YesNo
} from './visaAnalysisFormHelpers';
import { Button } from '../../components/UI';

const radioClass = 'h-5 w-5 shrink-0 text-primary-600 focus:ring-primary-500';

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required
}) => (
  <span className="visa-form-label">
    {children}
    {required && <span className="text-red-500"> *</span>}
  </span>
);

const YesNoGroup: React.FC<{
  name: string;
  value: YesNo;
  onChange: (v: YesNo) => void;
  label: string;
  yesLabel: string;
  noLabel: string;
}> = ({ name, value, onChange, label, yesLabel, noLabel }) => (
  <fieldset className="visa-form-field space-y-3">
    <legend className="visa-form-label mb-0">{label}</legend>
    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
      {(['sim', 'nao'] as const).map((opt) => (
        <label
          key={opt}
          className="inline-flex items-center gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 min-h-[52px] sm:min-h-[44px] sm:rounded-lg sm:border sm:px-3 sm:py-2.5 sm:bg-transparent has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
        >
          <input
            type="radio"
            name={name}
            checked={value === opt}
            onChange={() => onChange(opt)}
            className={radioClass}
          />
          <span className="text-base sm:text-sm font-medium text-gray-900">{opt === 'sim' ? yesLabel : noLabel}</span>
        </label>
      ))}
    </div>
  </fieldset>
);

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoComplete?: string;
}> = ({ label, value, onChange, type = 'text', required, placeholder, inputMode, autoComplete }) => (
  <label className="visa-form-field block">
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      autoComplete={autoComplete}
      className={`visa-form-input ${type === 'date' ? 'visa-form-input--date' : ''}`}
    />
  </label>
);

const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, value, onChange, required, children }) => (
  <label className="visa-form-field block">
    <FieldLabel required={required}>{label}</FieldLabel>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="visa-form-select"
    >
      {children}
    </select>
  </label>
);

const stepFieldsClass = 'space-y-5 sm:space-y-4 min-w-0';

const pageShell = 'min-h-[100dvh] bg-slate-100';

const VisaAnalysisForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const tf = (key: string) => t(`visaForm.${key}`);
  const fld = (key: string) => t(`visaForm.fields.${key}`);
  const yesLabel = t('common.yes');
  const noLabel = t('common.no');
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState<VisaResponses>(emptyResponses());
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [stepError, setStepError] = useState('');

  const steps = useMemo(
    () => buildWizardSteps(data.objective.mainGoal),
    [data.objective.mainGoal]
  );
  const currentStep = steps[stepIndex] ?? 'personal';
  const progress = steps.length > 1 ? ((stepIndex + 1) / steps.length) * 100 : 0;
  const displaySiteName = settings?.site?.name || siteName || 'NEIA';
  const reviewSections = useMemo(() => buildReviewSummary(data, t), [data, t, i18n.language]);

  useEffect(() => {
    if (!token) {
      setErrorMsg(tf('linkInvalid'));
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`/visa-analysis/public/${token}`);
        setValid(res.data.valid);
        setAlreadySubmitted(res.data.alreadySubmitted);
        setSiteName(res.data.siteName || 'NEIA');
      } catch (err: any) {
        setValid(false);
        setErrorMsg(err.response?.data?.message || tf('linkExpired'));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  useEffect(() => {
    if (stepIndex >= steps.length) setStepIndex(Math.max(0, steps.length - 1));
  }, [steps.length, stepIndex]);

  const patch = useCallback(
    <K extends keyof VisaResponses>(section: K, field: keyof VisaResponses[K], value: unknown) => {
      setData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    },
    []
  );

  const validateStep = (step: WizardStepId): string | null => {
    const err = (key: string) => t(`visaForm.errors.${key}`);
    if (step === 'personal') {
      if (!data.personal.fullName.trim() || data.personal.fullName.trim().length < 3) {
        return err('fullName');
      }
      if (!data.personal.birthDate) return err('birthDate');
      if (!data.personal.nationality.trim()) return err('nationality');
      if (!data.personal.maritalStatus) return err('maritalStatus');
      if (!data.personal.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return err('email');
      if (!data.personal.phone.trim() || data.personal.phone.trim().length < 6) {
        return err('phone');
      }
      if (!data.personal.hasValidPassport) return err('passport');
      if (data.personal.hasChildren === 'sim' && !data.personal.childrenCount.trim()) {
        return err('childrenCount');
      }
    }
    if (step === 'situation') {
      if (!data.currentSituation.inPortugal) return err('inPortugal');
      if (data.currentSituation.inPortugal === 'sim') {
        if (!data.currentSituation.enteredLegally) return err('enteredLegally');
        if (!data.currentSituation.hasEntryStamp) return err('entryStamp');
        if (!data.currentSituation.residencyStatus) return err('residencyStatus');
      }
      if (!data.currentSituation.hasAimaOrLegalProcess) {
        return err('aima');
      }
    }
    if (step === 'objective') {
      if (!data.objective.mainGoal) return err('mainGoal');
      if (data.objective.mainGoal === 'outro' && !data.objective.otherGoal.trim()) {
        return err('otherGoal');
      }
      if (!data.objective.bringFamily) return err('bringFamily');
      if (!data.objective.hasAccommodation) return err('accommodation');
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(currentStep);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError('');
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    setStepError('');
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const handleSubmit = async () => {
    const err = validateStep('personal');
    if (err) {
      setStepError(err);
      return;
    }
    if (!data.objective.mainGoal) {
      setStepError(t('visaForm.errors.mainGoal'));
      return;
    }
    setSubmitting(true);
    setStepError('');
    try {
      await axios.post(`/visa-analysis/public/${token}/submit`, data);
      setDone(true);
    } catch (e: any) {
      const msg =
        e.response?.data?.errors?.[0]?.msg ||
        e.response?.data?.message ||
        tf('submitError');
      setStepError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'personal':
        return (
          <div className={stepFieldsClass}>
            <TextField
              label={fld('fullName')}
              value={data.personal.fullName}
              onChange={(v) => patch('personal', 'fullName', v)}
              required
              autoComplete="name"
            />
            <TextField
              label={fld('birthDate')}
              type="date"
              value={data.personal.birthDate}
              onChange={(v) => patch('personal', 'birthDate', v)}
              required
            />
            <TextField
              label={fld('nationality')}
              value={data.personal.nationality}
              onChange={(v) => patch('personal', 'nationality', v)}
              required
              autoComplete="country-name"
            />
            <SelectField
              label={fld('maritalStatus')}
              value={data.personal.maritalStatus}
              onChange={(v) => patch('personal', 'maritalStatus', v)}
              required
            >
              <option value="">{tf('select')}</option>
              {(['solteiro', 'casado', 'divorciado', 'viuvo'] as const).map((s) => (
                <option key={s} value={s}>
                  {t(`visaForm.marital.${s}`)}
                </option>
              ))}
            </SelectField>
            <YesNoGroup
              name="children"
              label={fld('hasChildren')}
              value={data.personal.hasChildren}
              onChange={(v) => patch('personal', 'hasChildren', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            {data.personal.hasChildren === 'sim' && (
              <TextField
                label={fld('childrenCount')}
                value={data.personal.childrenCount}
                onChange={(v) => patch('personal', 'childrenCount', v)}
                required
              />
            )}
            <TextField
              label={fld('email')}
              type="email"
              value={data.personal.email}
              onChange={(v) => patch('personal', 'email', v)}
              required
              inputMode="email"
              autoComplete="email"
            />
            <TextField
              label={fld('phone')}
              type="tel"
              value={data.personal.phone}
              onChange={(v) => patch('personal', 'phone', v)}
              required
              inputMode="tel"
              autoComplete="tel"
            />
            <YesNoGroup
              name="passport"
              label={fld('hasValidPassport')}
              value={data.personal.hasValidPassport}
              onChange={(v) => patch('personal', 'hasValidPassport', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
          </div>
        );
      case 'situation':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup
              name="inPortugal"
              label={fld('inPortugal')}
              value={data.currentSituation.inPortugal}
              onChange={(v) => patch('currentSituation', 'inPortugal', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            {data.currentSituation.inPortugal === 'sim' && (
              <>
                <YesNoGroup
                  name="legal"
                  label={fld('enteredLegally')}
                  value={data.currentSituation.enteredLegally}
                  onChange={(v) => patch('currentSituation', 'enteredLegally', v)}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                />
                <YesNoGroup
                  name="stamp"
                  label={fld('hasEntryStamp')}
                  value={data.currentSituation.hasEntryStamp}
                  onChange={(v) => patch('currentSituation', 'hasEntryStamp', v)}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                />
                <fieldset className="visa-form-field space-y-3">
                  <legend className="visa-form-label mb-0">{tf('currentlyLegend')}</legend>
                  <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-4">
                    {(['regular', 'irregular'] as const).map((s) => (
                      <label
                        key={s}
                        className="inline-flex items-center gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 min-h-[52px] sm:min-h-0 sm:rounded-lg sm:border sm:px-3 sm:py-2.5 sm:bg-transparent has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
                      >
                        <input
                          type="radio"
                          className={radioClass}
                          checked={data.currentSituation.residencyStatus === s}
                          onChange={() => patch('currentSituation', 'residencyStatus', s)}
                        />
                        <span className="text-base sm:text-sm font-medium text-gray-900">
                          {t(`visaForm.residency.${s}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </>
            )}
            <YesNoGroup
              name="aima"
              label={fld('hasAimaProcess')}
              value={data.currentSituation.hasAimaOrLegalProcess}
              onChange={(v) => patch('currentSituation', 'hasAimaOrLegalProcess', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
          </div>
        );
      case 'objective':
        return (
          <div className={stepFieldsClass}>
            <fieldset className="visa-form-field min-w-0">
              <legend className="visa-form-label mb-3">
                {tf('mainGoalLegend')} <span className="text-red-500">*</span>
              </legend>
              <div className="space-y-2.5">
                {MAIN_GOAL_VALUES.map((value) => (
                  <label
                    key={value}
                    className={`flex items-start gap-3 p-4 sm:p-3 min-h-[52px] rounded-xl sm:rounded-lg border-2 sm:border cursor-pointer transition touch-manipulation ${
                      data.objective.mainGoal === value
                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500/20'
                        : 'border-gray-200 hover:border-gray-300 active:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="mainGoal"
                      checked={data.objective.mainGoal === value}
                      onChange={() => patch('objective', 'mainGoal', value)}
                      className={`${radioClass} mt-0.5`}
                    />
                    <span className="text-base sm:text-sm font-medium text-gray-900 leading-snug">
                      {t(`visaForm.goals.${value}`)}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {data.objective.mainGoal === 'outro' && (
              <TextField
                label={fld('otherGoal')}
                value={data.objective.otherGoal}
                onChange={(v) => patch('objective', 'otherGoal', v)}
                required
              />
            )}
            <YesNoGroup
              name="family"
              label={fld('bringFamily')}
              value={data.objective.bringFamily}
              onChange={(v) => patch('objective', 'bringFamily', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
            <YesNoGroup
              name="housing"
              label={fld('hasAccommodation')}
              value={data.objective.hasAccommodation}
              onChange={(v) => patch('objective', 'hasAccommodation', v)}
              yesLabel={yesLabel}
              noLabel={noLabel}
            />
          </div>
        );
      case 'workD1':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="offer" label={fld('hasJobOffer')} value={data.workD1.hasJobOffer} onChange={(v) => patch('workD1', 'hasJobOffer', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="contract" label={fld('hasContractOrPromise')} value={data.workD1.hasContractOrPromise} onChange={(v) => patch('workD1', 'hasContractOrPromise', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <TextField label={fld('profession')} value={data.workD1.profession} onChange={(v) => patch('workD1', 'profession', v)} />
            <YesNoGroup name="exp" label={fld('hasProvenExperience')} value={data.workD1.hasProvenExperience} onChange={(v) => patch('workD1', 'hasProvenExperience', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="cv" label={fld('hasUpdatedCv')} value={data.workD1.hasUpdatedCv} onChange={(v) => patch('workD1', 'hasUpdatedCv', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'studyD4':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="accepted" label={fld('acceptedByInstitution')} value={data.studyD4.acceptedByInstitution} onChange={(v) => patch('studyD4', 'acceptedByInstitution', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <TextField label={fld('intendedCourse')} value={data.studyD4.intendedCourse} onChange={(v) => patch('studyD4', 'intendedCourse', v)} />
            <YesNoGroup name="finance" label={fld('hasFinancialProof')} value={data.studyD4.hasFinancialProof} onChange={(v) => patch('studyD4', 'hasFinancialProof', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <TextField label={fld('studyFundingSource')} value={data.studyD4.studyFundingSource} onChange={(v) => patch('studyD4', 'studyFundingSource', v)} />
          </div>
        );
      case 'd7':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="passive" label={fld('hasPassiveIncome')} value={data.d7.hasPassiveIncome} onChange={(v) => patch('d7', 'hasPassiveIncome', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <TextField label={fld('incomeSource')} value={data.d7.incomeSource} onChange={(v) => patch('d7', 'incomeSource', v)} />
            <YesNoGroup name="prove" label={fld('canProveOfficially')} value={data.d7.canProveOfficially} onChange={(v) => patch('d7', 'canProveOfficially', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="savings" label={fld('hasSavings')} value={data.d7.hasSavings} onChange={(v) => patch('d7', 'hasSavings', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'd8':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="remote" label={fld('worksRemotely')} value={data.d8.worksRemotely} onChange={(v) => patch('d8', 'worksRemotely', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="foreign" label={fld('worksForForeignCompany')} value={data.d8.worksForForeignCompany} onChange={(v) => patch('d8', 'worksForForeignCompany', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <TextField label={fld('averageMonthlyIncome')} value={data.d8.averageMonthlyIncome} onChange={(v) => patch('d8', 'averageMonthlyIncome', v)} />
            <YesNoGroup name="proof" label={fld('hasContractsOrProof')} value={data.d8.hasContractsOrProof} onChange={(v) => patch('d8', 'hasContractsOrProof', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'd2':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="company" label={fld('hasCompany')} value={data.d2.hasCompany} onChange={(v) => patch('d2', 'hasCompany', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="openPt" label={fld('willOpenCompanyInPortugal')} value={data.d2.willOpenCompanyInPortugal} onChange={(v) => patch('d2', 'willOpenCompanyInPortugal', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="plan" label={fld('hasBusinessPlan')} value={data.d2.hasBusinessPlan} onChange={(v) => patch('d2', 'hasBusinessPlan', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="capital" label={fld('hasInvestmentCapital')} value={data.d2.hasInvestmentCapital} onChange={(v) => patch('d2', 'hasInvestmentCapital', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'familyReunion':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="relative" label={fld('hasLegalizedRelative')} value={data.familyReunion.hasLegalizedRelative} onChange={(v) => patch('familyReunion', 'hasLegalizedRelative', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <TextField label={fld('relationshipDegree')} value={data.familyReunion.relationshipDegree} onChange={(v) => patch('familyReunion', 'relationshipDegree', v)} />
            <fieldset className="visa-form-field space-y-3">
              <legend className="visa-form-label mb-0">{tf('relativeHasLegend')}</legend>
              <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-4">
                {(['residencia', 'nacionalidade'] as const).map((s) => (
                  <label
                    key={s}
                    className="inline-flex items-center gap-3 cursor-pointer rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 min-h-[52px] sm:min-h-0 sm:rounded-lg sm:border sm:px-3 sm:py-2.5 sm:bg-transparent has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
                  >
                    <input
                      type="radio"
                      className={radioClass}
                      checked={data.familyReunion.relativeStatus === s}
                      onChange={() => patch('familyReunion', 'relativeStatus', s)}
                    />
                    <span className="text-base sm:text-sm font-medium text-gray-900">
                      {t(`visaForm.relativeStatus.${s}`)}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        );
      case 'nationalityPt':
        return (
          <div className={stepFieldsClass}>
            <YesNoGroup name="ptRel" label={fld('hasPortugueseRelative')} value={data.nationalityPt.hasPortugueseRelative} onChange={(v) => patch('nationalityPt', 'hasPortugueseRelative', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="started" label={fld('processStarted')} value={data.nationalityPt.processStarted} onChange={(v) => patch('nationalityPt', 'processStarted', v)} yesLabel={yesLabel} noLabel={noLabel} />
            <YesNoGroup name="docs" label={fld('hasFamilyDocuments')} value={data.nationalityPt.hasFamilyDocuments} onChange={(v) => patch('nationalityPt', 'hasFamilyDocuments', v)} yesLabel={yesLabel} noLabel={noLabel} />
          </div>
        );
      case 'review':
        return (
          <div className={`${stepFieldsClass} text-gray-800`}>
            <p className="text-base sm:text-sm text-gray-600 leading-relaxed">{tf('reviewIntro')}</p>
            <div className="rounded-xl border-2 border-gray-100 bg-gray-50 p-4 sm:p-5 space-y-5 max-h-[min(50vh,480px)] sm:max-h-[28rem] overflow-y-auto overflow-x-hidden overscroll-contain">
              {reviewSections.map((section) => (
                <div key={section.title} className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base sm:text-base mb-2.5">{section.title}</h3>
                  <dl className="space-y-2.5">
                    {section.lines.map((row) => (
                      <div
                        key={`${section.title}-${row.label}`}
                        className="min-w-0 border-b border-gray-200/80 pb-2.5 last:border-0 last:pb-0"
                      >
                        <dt className="text-sm sm:text-xs font-medium text-gray-500 mb-0.5">{row.label}</dt>
                        <dd className="text-base sm:text-sm text-gray-900 font-medium break-words">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <Shield className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{tf('linkUnavailable')}</h1>
          <p className="text-gray-600">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (alreadySubmitted && !done) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-slate-50 p-4 sm:p-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-lg p-8">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{tf('alreadySubmittedTitle')}</h1>
          <p className="text-gray-600">{t('visaForm.alreadySubmittedMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className={`flex items-center justify-center p-4 sm:p-6 ${pageShell}`}>
        <div className="max-w-md w-full text-center bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <CheckCircle className="w-14 h-14 text-primary-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{tf('thankYou')}</h1>
          <p className="text-gray-600">{t('visaForm.successMsg', { siteName: displaySiteName })}</p>
        </div>
      </div>
    );
  }

  const navButtons = (
    <>
      <Button variant="ghost" className="flex-1 sm:flex-none"
        type="button"
        onClick={goBack}
        disabled={stepIndex === 0}>
        <ChevronLeft className="w-4 h-4 shrink-0" /> {tf('prev')}
      </Button>
      {currentStep === 'review' ? (
        <Button className="flex-1 sm:flex-none"
          type="button"
          onClick={handleSubmit}
          disabled={submitting}>
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {tf('submit')}
        </Button>
      ) : (
        <Button className="flex-1 sm:flex-none"
          type="button"
          onClick={goNext}>
          {tf('next')} <ChevronRight className="w-4 h-4 shrink-0" />
        </Button>
      )}
    </>
  );

  return (
    <div
      className={`${pageShell} pb-[max(1rem,env(safe-area-inset-bottom))]`}
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-8 lg:py-10">
        <header className="text-center text-gray-900 mb-5 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <SiteLogo
              size="lg"
              nameClassName="font-bold text-gray-900 text-xl sm:text-2xl"
              className="justify-center"
            />
          </div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight px-1">
            {tf('title')}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base mt-2 max-w-lg mx-auto px-2">
            {tf('subtitle')}
          </p>
          <div className="flex justify-center mt-4 lg:hidden">
            <LanguageSwitcher
              compact
              variant="floating"
              className="shadow-md"
            />
          </div>
        </header>

        <div className="flex flex-col items-stretch lg:flex-row lg:items-start lg:justify-center lg:gap-5 xl:gap-8">
          <div className="w-full max-w-2xl mx-auto lg:mx-0 min-w-0 flex-1">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-full">
              <div className="px-4 pt-4 sm:px-6 sm:pt-6">
                <div className="flex justify-between text-xs sm:text-sm text-gray-500 mb-2">
                  <span>{t('visaForm.stepOf', { current: stepIndex + 1, total: steps.length })}</span>
                  <span className="font-medium text-primary-600">{Math.round(progress)}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full bg-primary-600 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>

                <div
                  className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 lg:hidden scrollbar-none snap-x snap-mandatory"
                  aria-hidden
                >
                  {steps.map((step, i) => {
                    const active = i === stepIndex;
                    const done = i < stepIndex;
                    return (
                      <div
                        key={step}
                        className={`shrink-0 snap-center w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-colors ${
                          active
                            ? 'bg-primary-600 text-white'
                            : done
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {i + 1}
                      </div>
                    );
                  })}
                </div>

                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mt-3 sm:mt-4 leading-snug">
                  {t(`visaForm.steps.${currentStep}`)}
                </h2>
              </div>

              <div className="px-4 py-5 sm:px-6 sm:py-6 overflow-x-hidden min-w-0">
                {renderStep()}
              </div>

              {stepError && (
                <p className="px-4 sm:px-6 pb-2 text-base sm:text-sm font-medium text-red-600" role="alert">
                  {stepError}
                </p>
              )}

              <div className="hidden sm:flex px-6 pb-6 justify-between gap-3">{navButtons}</div>
            </div>

            <div
              className="fixed inset-x-0 bottom-0 z-30 sm:hidden border-t border-gray-200/90 bg-white/95 backdrop-blur-md px-4 pt-3 shadow-[0_-8px_30px_rgba(0,0,0,0.12)]"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex gap-3 max-w-2xl mx-auto">{navButtons}</div>
            </div>
            <div className="h-[5.5rem] sm:hidden" aria-hidden />

            <p className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6 px-2 pb-24 sm:pb-0">
              {tf('privacy')}
            </p>
          </div>

          <aside
            className="hidden lg:flex shrink-0 sticky top-8 self-start flex-col items-center"
            aria-label={t('visaForm.languagePanel', { defaultValue: 'Idioma' })}
          >
            <LanguageSwitcher orientation="vertical" variant="floating" compact />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default VisaAnalysisForm;
